/* eslint-disable import/order */

import { isAfter } from 'date-fns'
import merge from 'deepmerge'
import viewReducers from '../actions/views/viewReducers'

/**
 * JSON API Deserializer
 *
 * Takes a set of JSON-API formatted data and normalizes it
 * for use. One thing to keep in mind while reading through
 * this code is that the 'data' attribute in the payload
 * is sometimes an array of resources, and sometimes it is
 * a single object resource. This means that a lot of the
 * of the logic here needs to account for both of these cases.
 */

import { decorate, each, flatten } from './helpers'

/**
 * Takes a collection of entities and an entity.
 * Reconciles the entity relationships and adds
 * the entity to its proper collection.
 */
export const addEntityToCollection = (
  onlyUpdatedResources,
  entitiesByType,
  entity,
  isRelationship = false
) => {
  const updatedEntitiesByType = entitiesByType
  const updatedEntity = entity
  const entitiesOfSameType = entitiesByType[entity.type] || {}
  const originalEntity = entitiesOfSameType[entity.id]
  const originalUpdatedAt = originalEntity?.updated_at ?? null
  const preExistingRelationships =
    entitiesOfSameType[entity.id] && entitiesOfSameType[entity.id].relationships
  const flattenedItem = flatten(updatedEntity)

  if (
    onlyUpdatedResources &&
    originalUpdatedAt &&
    (!flattenedItem.updated_at ||
      !isAfter(flattenedItem.updated_at, originalUpdatedAt))
  ) {
    // shirt-circuit without updating when the existing local resource is at least as new
    // as the incoming "updated" resource.
    return entitiesByType
  }

  flattenedItem.relationships = {
    ...preExistingRelationships,
    ...flattenedItem.relationships,
  }
  let finalEntity = flattenedItem
  if (!isRelationship) {
    finalEntity = decorate(flattenedItem)
  }

  updatedEntitiesByType[updatedEntity.type] = {
    ...(entitiesByType[updatedEntity.type] || {}),
  }
  updatedEntitiesByType[updatedEntity.type][updatedEntity.id] = {
    ...updatedEntitiesByType[updatedEntity.type][updatedEntity.id],
    ...finalEntity,
  }

  return updatedEntitiesByType
}

/**
 * Takes an Array or Object and creates a new
 * object. The new object will have one or more
 * objects of types (e.g. 'users', 'phases').
 * Each of these will have the entities, keyed by
 * their ids.
 *
 * e.g.:
 * output: {
 *   users: {
 *     '123': {
 *       name: 'Cool Guy',
 *       id: '123',
 *       type: 'users',
 *     },
 *     '456': {
 *       name: 'Not So Cool Guy',
 *       id: '456',
 *       type: 'users',
 *     },
 *   },
 * }
 */
export const deserializeData = (
  onlyUpdatedResources,
  input,
  oldData = {},
  resourceType
) => {
  if (!input) {
    return {}
  }

  const payloadIndices = {
    ids: [],
    type: '',
  }

  if (Array.isArray(input)) {
    let newOutput = oldData
    payloadIndices.type = resourceType

    input.forEach((item) => {
      payloadIndices.ids.push(item.id)
      newOutput = addEntityToCollection(onlyUpdatedResources, newOutput, item)
    })

    return {
      jsonApiEntities: newOutput,
      payloadIndices,
    }
  }

  payloadIndices.type = resourceType
  payloadIndices.ids.push(input.id)
  return {
    jsonApiEntities: addEntityToCollection(
      onlyUpdatedResources,
      oldData,
      input
    ),
    payloadIndices,
  }
}

/**
 * Takes an entity and old state. Will build new state by
 * taking all of the entity's relationships and placing
 * them at the top level of jsonApiEntities.
 */
const addEntityRelationshipsToState = (
  onlyUpdatedResources,
  entity,
  oldState
) => {
  const newState = oldState
  each(entity.relationships, (relationship) => {
    if (!relationship.data || !relationship.data.id) {
      return
    }

    if (Array.isArray(relationship.data)) {
      each(relationship.data, (item) => {
        newState.jsonApiEntities = addEntityToCollection(
          onlyUpdatedResources,
          newState.jsonApiEntities,
          item,
          true
        )
      })
      return
    }

    newState.jsonApiEntities = addEntityToCollection(
      onlyUpdatedResources,
      newState.jsonApiEntities,
      relationship.data,
      true
    )
  })

  return newState
}

/**
 * Takes data and old state. Returns a new state after
 * processing the data's relationships.
 */
const addRelationshipsToState = (onlyUpdatedResources, data, oldState) => {
  if (!data) {
    return oldState
  }
  let newState = oldState

  if (Array.isArray(data)) {
    each(data, (item) => {
      // NOTE: Matt Polzin -> Am I reading this right that we are looping
      //       over an array of resources and only actually using the last
      //       one because we pass oldState in below rather than newState?
      newState = addEntityRelationshipsToState(
        onlyUpdatedResources,
        item,
        oldState
      )
    })
    return newState
  }

  newState = addEntityRelationshipsToState(onlyUpdatedResources, data, oldState)

  return newState
}

/**
 * Builds meta data object based off of the meta data
 * key in the response. In most implementations the
 * meta type is inferred from the entity data type.
 * If an entity data type is included in the meta
 * data, we use that instead.
 *
 * e.g. responses from server
 *
 * {
 *   data: [{ type: 'moment', relationships: [...] }],
 *   include: [...],
 *   meta: {
 *     page: {
 *       limit: 50,
 *       offset: 0
 *     },
 *     type: 'moment',
 *     total: 100,
 *   },
 * }
 *
 * {
 *   data: [],
 *   include: [],
 *   meta: {
 *     total: 10,
 *     type: 'phase_item',
 *     page: {
 *       limit: 50,
 *       offset: 0
 *     },
 *     aggregate: {
 *       overdue_total: 0
 *     }
 *   },
 * }
 */
const buildMetaData = (payload, resourceType) => {
  // there are cases in which a response can include
  // meta data but no data or includes. In these cases
  // the meta field should include the entity "type"
  // in order to properly deserialize the meta data.
  const hasData = payload.data.length || Object.keys(payload.data).length
  if (!hasData && !payload.meta) return {}

  return {
    [resourceType]: {
      ...payload.meta,
    },
  }
}

/**
 * Takes meta object and determines how most performantly
 * to merge the two objects into a single updated meta
 */

const mergeMeta = ({ preExistingMeta, newMeta }) => {
  if (!Object.keys(newMeta).length) return preExistingMeta
  if (!Object.keys(preExistingMeta).length) return newMeta
  return merge(preExistingMeta, newMeta)
}

/*
 * Takes a payload and determines the payload's
 * resource type based off of its data or
 * its metadata.
 */
export const getResourceType = (payload) => {
  const hasData = payload.data.length || Object.keys(payload.data).length
  const type = hasData
    ? payload.data.type || payload.data[0].type
    : payload.meta.resource_type
  return type
}

/**
 * Takes JSON-API formatted data and prepares it for use.
 * Will return state along with a prepared jsonApiEntities
 * object with all data deserialized.
 */
const deserializeJsonHandler =
  (onlyUpdatedResources) =>
  ({ state, payload }) => {
    let newState = { ...state }

    const payloadIndices = {
      ids: [],
      type: '',
    }

    if (payload.data.length === 0) {
      const resourceType = payload.entityType ?? payload.meta?.resource_type
      if (resourceType === undefined) return state

      if (payload.entityType !== undefined) {
        payloadIndices.type = payload.entityType
      }
      // An empty page still carries counts, such as the overdue items a
      // filter hid, so its meta must update the stored counts.
      if (payload.meta) {
        newState.jsonApiEntities = { ...(newState.jsonApiEntities || {}) }
        const preExistingMeta = newState.jsonApiEntities.meta || {}
        const newMeta = buildMetaData(payload, resourceType)
        newState.jsonApiEntities.meta = mergeMeta({ preExistingMeta, newMeta })
      }
    } else {
      newState.jsonApiEntities = { ...(newState.jsonApiEntities || {}) }
      const resourceType = getResourceType(payload)
      const preExistingMeta =
        (newState.jsonApiEntities && newState.jsonApiEntities.meta) || {}
      const newMeta = buildMetaData(payload, resourceType)
      const meta = mergeMeta({ preExistingMeta, newMeta })
      newState.jsonApiEntities.meta = meta

      newState = addRelationshipsToState(
        onlyUpdatedResources,
        payload.included,
        newState
      )
      newState = addRelationshipsToState(
        onlyUpdatedResources,
        payload.data,
        newState
      )

      const deserializedIncludes = payload.included
        ? deserializeData(
            onlyUpdatedResources,
            payload.included,
            newState.jsonApiEntities
          )
        : false

      newState.jsonApiEntities = deserializedIncludes
        ? deserializedIncludes.jsonApiEntities
        : newState.jsonApiEntities

      const { jsonApiEntities, payloadIndices: newPayloadIndices } =
        deserializeData(
          onlyUpdatedResources,
          payload.data,
          newState.jsonApiEntities,
          resourceType
        )

      payloadIndices.ids = newPayloadIndices.ids
      payloadIndices.type = newPayloadIndices.type

      newState.jsonApiEntities = jsonApiEntities
    }

    Object.keys(newState.entityDataByView || {}).forEach((view) => {
      const reducer = viewReducers[view]
      const data = reducer(newState.entityDataByView[view], {
        payload: {
          payloadIndices,
          topLevelState: newState,
          view: payload.view,
          ...(payload.viewKey && { viewKey: payload.viewKey }),
        },
        type: 'DESERIALIZER_INDEXING',
      })

      newState.entityDataByView[view] = data
    })

    return newState
  }

export const deserializeJson = deserializeJsonHandler(false)
export const deserializeUpdatedJson = deserializeJsonHandler(true)
